import React, { useState, useMemo, useCallback } from "react";
import { Form } from "react-bootstrap";
import { Calendar } from "react-bootstrap-icons";
import PropTypes from "prop-types";
import DatePickerModal from "../../components/DatePickerModal";
import "./TrendControlBar.css";

// Helper function for week display formatting
const formatWeekDisplay = (date) => {
  const d = new Date(date);
  const monthShort = d.toLocaleDateString('en-US', { month: 'short' });
  const day = d.getDate();
  return `${monthShort} ${day}`;
};

const TrendControlBar = ({
  selectedPeriod,
  onPeriodChange,
  startDate,
  endDate,
  onDateRangeChange,
  completedOnly,
  onCompletedOnlyChange
}) => {
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);

  const months = useMemo(() => [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ], []);

  // Convert YYYY-MM-DD to DatePickerModal format
  const getDateValueFromString = useCallback((dateStr, period) => {
    if (!dateStr) return null;
    // Parse date string manually to avoid timezone issues
    const [yearStr, monthStr] = dateStr.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1; // Convert to 0-based month
    const quarter = Math.floor(month / 3) + 1;


    switch (period) {
      case 'monthly':
        return {
          year,
          month,
          display: `${months[month]} ${year}`,
          shortDisplay: `${String(month + 1).padStart(2, '0')}/${year}`
        };
      case 'weekly': {
        const date = new Date(dateStr);
        return {
          date: dateStr,
          display: formatWeekDisplay(date),
          shortDisplay: formatWeekDisplay(date)
        };
      }
      case 'quarterly':
        return {
          year,
          quarter,
          display: `Q${quarter} ${year}`,
          shortDisplay: `Q${quarter} ${year}`
        };
      case 'yearly':
        return {
          year,
          display: `${year}`,
          shortDisplay: `${year}`
        };
      default:
        return null;
    }
  }, [months]);

  // Convert DatePickerModal value to YYYY-MM-DD
  const getStringFromDateValue = (value) => {
    if (!value) return '';
    const { year, month, quarter, date } = value;

    // For weekly: use the date directly
    if (date !== undefined) {
      return date;
    }

    let dateStr = `${year}-`;
    if (month !== undefined) {
      // For monthly: use the selected month
      dateStr += `${String(month + 1).padStart(2, '0')}-01`;
    } else if (quarter !== undefined) {
      // For quarterly: use the first month of the quarter
      const quarterMonth = (quarter - 1) * 3 + 1;
      dateStr += `${String(quarterMonth).padStart(2, '0')}-01`;
    } else {
      // For yearly: use January 1st
      dateStr += '01-01';
    }

    return dateStr;
  };

  // Get next period date (for adjusting end date when start date is too late)
  const getNextPeriodDate = useCallback((dateStr, period) => {
    const [yearStr, monthStr] = dateStr.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1; // Convert to 0-based month

    switch (period) {
      case 'monthly': {
        const nextMonth = month + 1;
        if (nextMonth > 11) {
          return `${year + 1}-01-01`;
        } else {
          return `${year}-${String(nextMonth + 1).padStart(2, '0')}-01`;
        }
      }
      case 'weekly': {
        // Add 7 days
        const date = new Date(dateStr);
        const nextWeek = new Date(date);
        nextWeek.setDate(date.getDate() + 7);
        return nextWeek.toISOString().split('T')[0];
      }
      case 'quarterly': {
        const currentQuarter = Math.floor(month / 3);
        const nextQuarter = currentQuarter + 1;
        if (nextQuarter > 3) {
          return `${year + 1}-01-01`;
        } else {
          const nextQuarterMonth = nextQuarter * 3 + 1;
          return `${year}-${String(nextQuarterMonth).padStart(2, '0')}-01`;
        }
      }
      case 'yearly':
        return `${year + 1}-01-01`;
      default:
        return dateStr;
    }
  }, []);

  // Get previous period date (for adjusting start date when end date is too early)
  const getPreviousPeriodDate = useCallback((dateStr, period) => {
    const [yearStr, monthStr] = dateStr.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1; // Convert to 0-based month

    switch (period) {
      case 'monthly': {
        const prevMonth = month - 1;
        if (prevMonth < 0) {
          return `${year - 1}-12-01`;
        } else {
          return `${year}-${String(prevMonth + 1).padStart(2, '0')}-01`;
        }
      }
      case 'weekly': {
        // Subtract 7 days
        const date = new Date(dateStr);
        const prevWeek = new Date(date);
        prevWeek.setDate(date.getDate() - 7);
        return prevWeek.toISOString().split('T')[0];
      }
      case 'quarterly': {
        const currentQuarter = Math.floor(month / 3);
        const prevQuarter = currentQuarter - 1;
        if (prevQuarter < 0) {
          return `${year - 1}-10-01`; // Q4 of previous year starts in October
        } else {
          const prevQuarterMonth = prevQuarter * 3 + 1;
          return `${year}-${String(prevQuarterMonth).padStart(2, '0')}-01`;
        }
      }
      case 'yearly':
        return `${year - 1}-01-01`;
      default:
        return dateStr;
    }
  }, []);

  const periods = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const getQuickSelectOptions = () => {
    const allOptions = [
      {
        label: 'Last 3 mo',
        getValue: () => {
          const end = new Date();
          const start = new Date();
          start.setMonth(end.getMonth() - 3);
          return {
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
          };
        }
      },
      {
        label: 'Last 6 mo',
        getValue: () => {
          const end = new Date();
          const start = new Date();
          start.setMonth(end.getMonth() - 6);
          return {
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
          };
        }
      },
      {
        label: 'Past Year',
        getValue: () => {
          if (selectedPeriod === 'quarterly') {
            // For quarterly, maintain the same quarter but go back one year
            const [endYearStr, endMonthStr] = endDate.split('-');
            const endYear = parseInt(endYearStr, 10);
            const endMonth = parseInt(endMonthStr, 10) - 1; // Convert to 0-based
            const endQuarter = Math.floor(endMonth / 3) + 1;

            // Start date: same quarter, previous year
            const startYear = endYear - 1;
            const startQuarterMonth = (endQuarter - 1) * 3 + 1; // First month of quarter
            const startDateStr = `${startYear}-${String(startQuarterMonth).padStart(2, '0')}-01`;

            return {
              startDate: startDateStr,
              endDate: endDate
            };
          } else {
            // Default behavior for monthly/yearly
            const end = new Date();
            const start = new Date();
            start.setFullYear(end.getFullYear() - 1);
            return {
              startDate: start.toISOString().split('T')[0],
              endDate: end.toISOString().split('T')[0]
            };
          }
        }
      }
    ];

    // Filter options based on selected period
    switch (selectedPeriod) {
      case 'monthly':
      case 'weekly':
        // Show all options for monthly and weekly
        return allOptions;
      case 'quarterly':
        // Only show "Past Year" for quarterly
        return allOptions.filter(option => option.label === 'Past Year');
      case 'yearly':
        // No quick select buttons for yearly
        return [];
      default:
        return allOptions;
    }
  };

  const quickSelectOptions = getQuickSelectOptions();

  const handleQuickSelect = (option) => {
    const dateRange = option.getValue();
    onDateRangeChange(dateRange.startDate, dateRange.endDate);
  };

  // Memoize date values to prevent unnecessary recalculations
  const startDateValue = useMemo(() =>
    getDateValueFromString(startDate, selectedPeriod),
    [startDate, selectedPeriod, getDateValueFromString]
  );

  const endDateValue = useMemo(() =>
    getDateValueFromString(endDate, selectedPeriod),
    [endDate, selectedPeriod, getDateValueFromString]
  );

  // Memoize display values
  const startDateDisplay = useMemo(() => {
    const display = startDateValue ? startDateValue.shortDisplay : '';
    return display;
  }, [startDateValue]);

  const endDateDisplay = useMemo(() => {
    const display = endDateValue ? endDateValue.shortDisplay : '';
    return display;
  }, [endDateValue]);


  return (
    <div className="trend-control-bar">
      <div className="trend-control-left">
        {/* Period Selector */}
        <div className="period-selector">
          <Form.Select
            value={selectedPeriod}
            onChange={(e) => onPeriodChange(e.target.value)}
            className="period-select"
          >
            {periods.map((period) => (
              <option key={period.value} value={period.value}>
                {period.label}
              </option>
            ))}
          </Form.Select>
        </div>

        {/* Date Range Picker */}
        <div className="date-range-picker">
          <div className="date-range-display">
            {selectedPeriod === 'weekly' ? (
              <div className="date-range-inputs" style={{ position: 'relative' }}>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    const newStartDate = e.target.value;
                    if (newStartDate && new Date(newStartDate) < new Date(endDate)) {
                      onDateRangeChange(newStartDate, endDate);
                    } else if (newStartDate) {
                      // If start date >= end date, adjust end date to be 7 days later
                      const adjustedEndDate = getNextPeriodDate(newStartDate, 'weekly');
                      onDateRangeChange(newStartDate, adjustedEndDate);
                    }
                  }}
                  className="trend-date-display start-date"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    width: 'auto',
                    WebkitAppearance: 'textfield',
                    MozAppearance: 'textfield'
                  }}
                />
                <span className="date-separator">-</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    const newEndDate = e.target.value;
                    if (newEndDate && new Date(newEndDate) > new Date(startDate)) {
                      onDateRangeChange(startDate, newEndDate);
                    } else if (newEndDate) {
                      // If end date <= start date, adjust start date to be 7 days earlier
                      const adjustedStartDate = getPreviousPeriodDate(newEndDate, 'weekly');
                      onDateRangeChange(adjustedStartDate, newEndDate);
                    }
                  }}
                  className="trend-date-display end-date"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    width: 'auto',
                    WebkitAppearance: 'textfield',
                    MozAppearance: 'textfield'
                  }}
                />
              </div>
            ) : (
              <div className="date-range-inputs" style={{ position: 'relative' }}>
                <div
                  className="trend-date-display start-date"
                  onClick={() => setShowStartDatePicker(true)}
                >
                  {startDateDisplay}
                </div>
                <span className="date-separator">-</span>
                <div
                  className="trend-date-display end-date"
                  onClick={() => setShowEndDatePicker(true)}
                >
                  {endDateDisplay}
                </div>
                <div className="calendar-icon">
                  <Calendar size={14} color="#000000" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Select Buttons */}
        <div className="quick-select-buttons">
          {quickSelectOptions.map((option, index) => (
            <button
              key={index}
              className="quick-select-btn"
              onClick={() => handleQuickSelect(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="trend-control-right">
        {/* Completed Only Checkbox */}
        <div className="completed-only-container">
          <Form.Check
            type="checkbox"
            id="completed-only-trend"
            label="Completed Only"
            checked={completedOnly}
            onChange={(e) => onCompletedOnlyChange(e.target.checked)}
            className="completed-only-checkbox"
          />
        </div>
      </div>

      {/* Start Date Picker */}
      <DatePickerModal
        show={showStartDatePicker}
        onHide={() => setShowStartDatePicker(false)}
        period={selectedPeriod}
        currentValue={startDateValue}
        onSelect={(value) => {
          const dateStr = getStringFromDateValue(value);
          // Ensure startDate is before endDate
          if (new Date(dateStr) >= new Date(endDate)) {
            // If selected start date is after or equal to end date,
            // adjust end date to be one period after start date
            const adjustedEndDate = getNextPeriodDate(dateStr, selectedPeriod);
            onDateRangeChange(dateStr, adjustedEndDate);
          } else {
            onDateRangeChange(dateStr, endDate);
          }
          setShowStartDatePicker(false);
        }}
      />

      {/* End Date Picker */}
      <DatePickerModal
        show={showEndDatePicker}
        onHide={() => setShowEndDatePicker(false)}
        period={selectedPeriod}
        currentValue={endDateValue}
        onSelect={(value) => {
          const dateStr = getStringFromDateValue(value);
          // Ensure endDate is after startDate
          if (new Date(dateStr) <= new Date(startDate)) {
            // If selected end date is before or equal to start date,
            // adjust start date to be one period before end date
            const adjustedStartDate = getPreviousPeriodDate(dateStr, selectedPeriod);
            onDateRangeChange(adjustedStartDate, dateStr);
          } else {
            onDateRangeChange(startDate, dateStr);
          }
          setShowEndDatePicker(false);
        }}
      />
    </div>
  );
};

TrendControlBar.propTypes = {
  selectedPeriod: PropTypes.string.isRequired,
  onPeriodChange: PropTypes.func.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  completedOnly: PropTypes.bool.isRequired,
  onCompletedOnlyChange: PropTypes.func.isRequired,
};

export default TrendControlBar;